import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { FormDialog } from '@/components/shared/form-dialog';
import { FormField } from '@/components/shared/form-field';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Listbox } from '@/components/ui/listbox';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { resolveApiError } from '@/lib/api-errors';
import {
  CHURCH_SUPPORT_PRIORITIES,
  SUPPORT_TICKET_CATEGORIES,
  type ChurchSupportPriority,
  type SupportTicketCategory,
  type SupportTicketSummary,
} from '@/pages/support/types';
import { SupportAttachmentPicker } from '@/pages/support/support-attachment-picker';
import { useSupportTicketUploads } from '@/pages/support/use-support-ticket-uploads';

export const CreateSupportTicketDialog = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { t } = useTranslation('support');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState<SupportTicketCategory>('other');
  const [priority, setPriority] = useState<ChurchSupportPriority>('normal');
  const [body, setBody] = useState('');
  const uploads = useSupportTicketUploads();

  const resetForm = () => {
    setSubject('');
    setCategory('other');
    setPriority('normal');
    setBody('');
    uploads.reset();
  };

  const create = useMutation({
    mutationFn: () =>
      api.post<SupportTicketSummary>('/support/tickets', {
        subject: subject.trim(),
        category,
        priority,
        body: body.trim(),
        attachments: uploads.attachments,
      }),
    onSuccess: (ticket) => {
      toast.success(t('create.success'));
      void queryClient.invalidateQueries({ queryKey: ['support', 'tickets'] });
      onOpenChange(false);
      resetForm();
      void navigate(`/support/tickets/${ticket.id}`);
    },
    onError: (err) => toast.error(resolveApiError(err, tErrors)),
  });

  const canSubmit =
    subject.trim().length >= 3 && body.trim().length >= 1 && !create.isPending && !uploads.uploading;

  return (
    <FormDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) resetForm();
        onOpenChange(next);
      }}
      size="md"
      title={t('create.title')}
      description={t('create.description')}
      submitLabel={t('create.submit')}
      cancelLabel={tCommon('action.cancel')}
      hideFooter
      submitting={create.isPending}
      submitDisabled={!canSubmit}
      onSubmit={() => create.mutate()}
      bodyClassName="overflow-visible"
    >
      <FormField label={t('create.subject')} required>
        {(field) => (
          <Input
            id={field.id}
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
            maxLength={200}
            autoFocus
          />
        )}
      </FormField>
      <FormField label={t('create.category')} required>
        {(field) => (
          <Listbox
            id={field.id}
            popoverPortal="dialog"
            value={category}
            onChange={(value) => setCategory(value as SupportTicketCategory)}
            options={SUPPORT_TICKET_CATEGORIES.map((value) => ({
              value,
              label: t(`category.${value}`),
            }))}
          />
        )}
      </FormField>
      <FormField label={t('create.priority')} required hint={t('create.priorityHint')}>
        {(field) => (
          <Listbox
            id={field.id}
            popoverPortal="dialog"
            value={priority}
            onChange={(value) => setPriority(value as ChurchSupportPriority)}
            options={CHURCH_SUPPORT_PRIORITIES.map((value) => ({
              value,
              label: t(`priority.${value}`),
            }))}
          />
        )}
      </FormField>
      <FormField label={t('create.body')} required>
        {(field) => (
          <Textarea
            id={field.id}
            value={body}
            onChange={(event) => setBody(event.target.value)}
            rows={5}
            maxLength={10_000}
          />
        )}
      </FormField>
      <div className="flex items-center gap-3">
        <SupportAttachmentPicker
          inlineHint
          files={uploads.pending}
          uploading={uploads.uploading}
          progress={uploads.progress}
          error={uploads.error}
          disabled={uploads.uploading || uploads.atLimit}
          onFiles={(files) => void uploads.addFiles(files)}
          onRemove={uploads.removeAt}
        />
        <Button
          type="submit"
          className="ms-auto shrink-0"
          loading={create.isPending}
          disabled={!canSubmit}
        >
          {t('create.submit')}
        </Button>
      </div>
    </FormDialog>
  );
};
